// Turns an HTTP failure into one short Chinese sentence for the page.
// Backend messages that are already Chinese win; anything else (English,
// masked 500s, HTML error pages, raw JSON) falls back to a status message.
const CJK = /[一-鿿]/;

export const NETWORK_ERROR_MESSAGE = '无法连接服务器，请检查网络后重试';

export function statusErrorMessage(status: number): string {
    if (status === 400 || status === 422) return '提交的内容有误，请检查后重试';
    if (status === 401) return '登录已过期，请重新登录';
    if (status === 403) return '没有权限执行此操作';
    if (status === 404) return '内容不存在或已删除';
    if (status === 409) return '数据已变更，请刷新后重试';
    if (status === 413) return '文件太大，请压缩后重试';
    if (status === 429) return '操作太频繁，请稍后再试';
    if (status >= 500) return '服务器出错，请稍后重试';
    return '操作失败，请重试';
}

function chineseText(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const text = value.trim();
    // A plain-text body may be an HTML error page; never show markup.
    return text && CJK.test(text) && !text.startsWith('<') ? text : null;
}

export function apiErrorMessage(status: number, body: unknown): string {
    const data = body && typeof body === 'object' ? (body as Record<string, any>) : null;
    const message = (data
        ? chineseText(data.error) ?? chineseText(data.error?.message) ?? chineseText(data.message)
        : chineseText(body)) ?? statusErrorMessage(status);
    const requestId = typeof data?.request_id === 'string' ? data.request_id : null;
    // The ID lets the team find the real cause in the server logs.
    return status >= 500 && requestId ? `${message}（错误编号 ${requestId}）` : message;
}

export async function readErrorBody(res: Response): Promise<unknown> {
    const text = await res.text().catch(() => '');
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

// For errors caught in components: keep a clean Chinese message, else the caller's fallback.
export function chineseErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof Error) || !error.message) return fallback;
    let parsed: unknown = null;
    try {
        parsed = JSON.parse(error.message);
    } catch {
        // Not JSON — a plain message.
    }
    if (parsed && typeof parsed === 'object') {
        // Older call sites threw the raw response body.
        const message = apiErrorMessage(0, parsed);
        return message === statusErrorMessage(0) ? fallback : message;
    }
    return chineseText(error.message) ?? fallback;
}
